import type {
  ApiResponse,
  PagedResult,
  LoginRequest,
  LoginResponse,
  MeResponse,
  Local,
  CreateLocalRequest,
  UpdateLocalRequest,
  Mesa,
  CreateMesaRequest,
  UpdateMesaRequest,
  Cliente,
  CreateClienteRequest,
  UpdateClienteRequest,
  BloquearClienteRequest,
  Reserva,
  CreateReservaRequest,
  UpdateReservaRequest,
  ConfirmarCancelarReservaRequest,
  ReservaListFilter,
  HistoricoMesa,
  CreateHistoricoMesaRequest,
  UpdateHistoricoMesaRequest,
  Configuracao,
  CreateConfiguracaoRequest,
  UpdateConfiguracaoRequest,
  Utilizador,
  CreateUtilizadorRequest,
  UpdateUtilizadorRequest,
  MapaMesasResponse,
  Auditoria,
  AuditoriaFilter,
  Canal,
  CreateCanalRequest,
  UpdateCanalRequest,
  EstatisticasResponse,
  PermissaoDto,
  RoleDto,
} from '../types';

export type { PermissaoDto, RoleDto } from '../types';

// Use Vite env variable for API base URL, fallback to relative for production
export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';
const REQUEST_TIMEOUT_MS = 30000;

class ApiError extends Error {
  constructor(
    message: string,
    public errorCode?: string,
    public status: number = 400
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

function notifyUnauthorized(endpoint: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT, { detail: { endpoint } }));
  }
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<ApiResponse<T>> {
  const { timeoutMs = REQUEST_TIMEOUT_MS, ...init } = options;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  // Permite cancelar a partir de fora combinando sinais
  const externalSignal = init.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...init,
      cache: 'no-store' as RequestCache,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        ...init.headers,
      } as HeadersInit,
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (err) {
    window.clearTimeout(timeout);
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError('Tempo limite excedido. Tente novamente.', 'TIMEOUT', 408);
    }
    throw new ApiError('Falha de rede. Verifique a ligação.', 'NETWORK_ERROR', 0);
  }
  window.clearTimeout(timeout);

  // Handle empty responses (e.g., 204 No Content)
  const contentType = response.headers.get('content-type');
  const hasJsonBody = contentType && contentType.includes('application/json');

  let data: ApiResponse<T> | null = null;
  if (hasJsonBody) {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const error = new ApiError(
      data?.message || 'Erro na requisição',
      data?.errorCode,
      response.status
    );
    // Log apenas em dev para não poluir consola em produção
    if (import.meta.env.DEV) {
      console.error(`[API Error] ${options.method || 'GET'} ${endpoint}:`, {
        status: response.status,
        message: error.message,
        errorCode: error.errorCode,
      });
    }
    if (response.status === 401 && endpoint !== '/auth/me' && endpoint !== '/auth/login') {
      notifyUnauthorized(endpoint);
    }
    throw error;
  }

  return data ?? ({ success: true, data: null, message: 'OK' } as ApiResponse<T>);
}

export const authApi = {
  login: (request: LoginRequest) =>
    fetchWithAuth<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  logout: () =>
    fetchWithAuth<void>('/auth/logout', { method: 'POST' }),

  me: () =>
    fetchWithAuth<MeResponse>('/auth/me'),

  updatePerfil: (request: { nome?: string; currentPassword?: string; newPassword?: string }) =>
    fetchWithAuth<void>('/auth/perfil', {
      method: 'PUT',
      body: JSON.stringify(request),
    }),
};

export const locaisApi = {
  getAll: () =>
    fetchWithAuth<Local[]>('/locais'),

  getById: (id: number) =>
    fetchWithAuth<Local>(`/locais/${id}`),

  create: (request: CreateLocalRequest) =>
    fetchWithAuth<Local>('/locais', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateLocalRequest) =>
    fetchWithAuth<Local>(`/locais/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/locais/${id}`, { method: 'DELETE' }),
};

export const mesasApi = {
  getAll: () =>
    fetchWithAuth<Mesa[]>('/mesas'),

  getByLocal: (localId: number) =>
    fetchWithAuth<Mesa[]>(`/mesas/local/${localId}`),

  getById: (id: number) =>
    fetchWithAuth<Mesa>(`/mesas/${id}`),

  create: (request: CreateMesaRequest) =>
    fetchWithAuth<Mesa>('/mesas', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateMesaRequest) =>
    fetchWithAuth<Mesa>(`/mesas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/mesas/${id}`, { method: 'DELETE' }),
};

export const clientesApi = {
  getAll: (search?: string, page = 1, pageSize = 50) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    return fetchWithAuth<PagedResult<Cliente>>(`/clientes?${params}`);
  },

  search: (q: string) =>
    fetchWithAuth<Cliente[]>(`/clientes/pesquisa?q=${encodeURIComponent(q)}`),

  getById: (id: number) =>
    fetchWithAuth<Cliente>(`/clientes/${id}`),

  create: (request: CreateClienteRequest) =>
    fetchWithAuth<Cliente>('/clientes', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateClienteRequest) =>
    fetchWithAuth<Cliente>(`/clientes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  bloquear: (id: number, request: BloquearClienteRequest) =>
    fetchWithAuth<Cliente>(`/clientes/${id}/bloquear`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/clientes/${id}`, { method: 'DELETE' }),
};

export const reservasApi = {
  getAll: (filter: ReservaListFilter) => {
    const params = new URLSearchParams();
    if (filter.data) params.append('data', filter.data);
    if (filter.apenasHoje) params.append('apenasHoje', 'true');
    if (filter.incluirCanceladas) params.append('incluirCanceladas', 'true');
    if (filter.incluirFechadas) params.append('incluirFechadas', 'true');
    params.append('page', filter.page.toString());
    params.append('pageSize', filter.pageSize.toString());
    return fetchWithAuth<PagedResult<Reserva>>(`/reservas?${params}`);
  },

  getMapaMesas: (data?: Date) => {
    const params = new URLSearchParams();
    if (data) {
      // data em formato local (yyyy-MM-dd) — evita desvio de dia do toISOString (UTC)
      const pad = (n: number) => String(n).padStart(2, '0');
      params.append('data', `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`);
    }
    return fetchWithAuth<MapaMesasResponse>(`/reservas/mapa-mesas?${params}`);
  },

  getById: (id: number) =>
    fetchWithAuth<Reserva>(`/reservas/${id}`),

  create: (request: CreateReservaRequest) =>
    fetchWithAuth<Reserva>('/reservas', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateReservaRequest) =>
    fetchWithAuth<Reserva>(`/reservas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  confirmar: (id: number, request: ConfirmarCancelarReservaRequest) =>
    fetchWithAuth<Reserva>(`/reservas/${id}/confirmar`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  cancelar: (id: number, request: ConfirmarCancelarReservaRequest) =>
    fetchWithAuth<Reserva>(`/reservas/${id}/cancelar`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/reservas/${id}`, { method: 'DELETE' }),
};

export const historicoMesasApi = {
  getAll: (data?: string, page = 1, pageSize = 50) => {
    const params = new URLSearchParams();
    if (data) params.append('data', data);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    return fetchWithAuth<PagedResult<HistoricoMesa>>(`/historico-mesas?${params}`);
  },

  getById: (id: number) =>
    fetchWithAuth<HistoricoMesa>(`/historico-mesas/${id}`),

  create: (request: CreateHistoricoMesaRequest) =>
    fetchWithAuth<HistoricoMesa>('/historico-mesas', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateHistoricoMesaRequest) =>
    fetchWithAuth<HistoricoMesa>(`/historico-mesas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/historico-mesas/${id}`, { method: 'DELETE' }),

  libertar: (id: number) =>
    fetchWithAuth<void>(`/historico-mesas/${id}/libertar`, { method: 'POST' }),
};

export const configuracoesApi = {
  getAll: () =>
    fetchWithAuth<Configuracao[]>('/configuracoes'),

  getById: (id: number) =>
    fetchWithAuth<Configuracao>(`/configuracoes/${id}`),

  getByChave: (chave: string) =>
    fetchWithAuth<Configuracao>(`/configuracoes/chave/${chave}`),

  create: (request: CreateConfiguracaoRequest) =>
    fetchWithAuth<Configuracao>('/configuracoes', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateConfiguracaoRequest) =>
    fetchWithAuth<Configuracao>(`/configuracoes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/configuracoes/${id}`, { method: 'DELETE' }),
};

export const utilizadoresApi = {
  getAll: () =>
    fetchWithAuth<Utilizador[]>('/utilizadores'),

  getById: (id: number) =>
    fetchWithAuth<Utilizador>(`/utilizadores/${id}`),

  create: (request: CreateUtilizadorRequest) =>
    fetchWithAuth<Utilizador>('/utilizadores', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  update: (id: number, request: UpdateUtilizadorRequest) =>
    fetchWithAuth<Utilizador>(`/utilizadores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  delete: (id: number) =>
    fetchWithAuth<void>(`/utilizadores/${id}`, { method: 'DELETE' }),
};

export const auditoriaApi = {
  getAll: (filter: AuditoriaFilter) => {
    const params = new URLSearchParams();
    if (filter.tabela) params.append('tabela', filter.tabela);
    if (filter.registoId) params.append('registoId', filter.registoId.toString());
    if (filter.dataInicio) params.append('dataInicio', filter.dataInicio);
    if (filter.dataFim) params.append('dataFim', filter.dataFim);
    params.append('page', filter.page.toString());
    params.append('pageSize', filter.pageSize.toString());
    return fetchWithAuth<PagedResult<Auditoria>>(`/auditoria?${params}`);
  },
};

export const permissoesApi = {
  getAll: (role?: number) => {
    const params = new URLSearchParams();
    if (role !== undefined) params.append('role', role.toString());
    const qs = params.toString();
    return fetchWithAuth<PermissaoDto[]>(qs ? `/permissoes?${qs}` : '/permissoes');
  },
  update: (id: number, dto: PermissaoDto) =>
    fetchWithAuth<PermissaoDto>(`/permissoes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }),
};

export const canaisApi = {
  getAll: () => fetchWithAuth<Canal[]>('/canais'),
  create: (dto: CreateCanalRequest) => fetchWithAuth<Canal>('/canais', { method: 'POST', body: JSON.stringify(dto) }),
  update: (id: number, dto: UpdateCanalRequest) => fetchWithAuth<Canal>(`/canais/${id}`, { method: 'PUT', body: JSON.stringify(dto) }),
  delete: (id: number) => fetchWithAuth<void>(`/canais/${id}`, { method: 'DELETE' }),
};

export const rolesApi = {
  getAll: () => fetchWithAuth<RoleDto[]>('/roles'),
  getById: (id: number) => fetchWithAuth<RoleDto>(`/roles/${id}`),
  create: (dto: { nome: string; descricao?: string }) => fetchWithAuth<RoleDto>('/roles', { method: 'POST', body: JSON.stringify(dto) }),
  update: (id: number, dto: { nome: string; descricao?: string; ativo: boolean; versao: number }) => fetchWithAuth<RoleDto>(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(dto) }),
  delete: (id: number) => fetchWithAuth<void>(`/roles/${id}`, { method: 'DELETE' }),
};

export const estatisticasApi = {
  get: (inicio: string, fim: string) => fetchWithAuth<EstatisticasResponse>(`/estatisticas?inicio=${inicio}&fim=${fim}`),
};

export const versionApi = {
  get: () => fetchWithAuth<{ version: string; informacionalVersion?: string }>('/version'),
};

export interface DbInfo {
  dbPath: string;
  size: number;
  tables: string[];
  counts: Record<string, number>;
}

export interface TableData {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  total: number;
  page: number;
  pageSize: number;
}

export const adminDbApi = {
  tables: async (): Promise<ApiResponse<string[]>> => {
    const info = await fetchWithAuth<DbInfo>('/admin/db/info');
    if (!info.success || !info.data) return { success: false, message: info.message || 'Erro' };
    return { success: true, data: info.data.tables };
  },
  info: () => fetchWithAuth<DbInfo>('/admin/db/info'),
  schema: (table: string) => fetchWithAuth<unknown[]>(`/admin/db/schema/${encodeURIComponent(table)}`),
  query: (table: string, page: number, pageSize: number) => fetchWithAuth<{ items: unknown[]; totalCount: number }>(`/admin/db/table/${encodeURIComponent(table)}?page=${page}&pageSize=${pageSize}`),
  table: (table: string, page: number, pageSize = 50) =>
    fetchWithAuth<TableData>(`/admin/db/tables/${encodeURIComponent(table)}?page=${page}&pageSize=${pageSize}`),
  rawQuery: (sql: string) => fetchWithAuth<unknown>('/admin/db/query', { method: 'POST', body: JSON.stringify({ sql }) }),
};

export { ApiError };